/**
 * src/components/spatial/spatialFlagIntentCache.ts
 *
 * Which buildings are wearing a flag, and how, remembered across refreshes.
 *
 * The bulk viewport response is a *summary*: it carries no `metadata` bag
 * at all. The flag intent lives in `metadata.surface_flag`, so it arrives
 * only on the per-object detail fetch — and the 30-second bulk refresh
 * then replaces the enriched feature with one that has no flag. Because
 * the detail fetch is de-duplicated for the session, the flag never came
 * back. That is the "it showed for a second and now I can't get it to
 * show again" bug, and it is the same problem tiers already solve the
 * same way: cache per object id, persist, and re-apply while normalising.
 *
 * The chosen bands are cached with the country, not just the country code.
 * A flag has several defensible arrangements, so a code alone cannot say
 * which one the operator picked, and re-deriving would repaint the tower
 * in something other than what was previewed.
 *
 * Absence and denial are deliberately not the same thing. A feature with
 * no metadata is a summary saying nothing; a feature whose metadata is
 * present but carries no flag is an object that genuinely has none, and
 * only the second clears the cache. Without that distinction the summary
 * would erase every flag on the next poll.
 */

import { parseFlagLayout, type FlagLayout } from '../../utils/flagArrangements';

export interface FlagIntent {
  /** ISO-3166-1 alpha-2, upper case. */
  countryCode: string;
  /** Which arrangement of the flag was chosen, if one was recorded. */
  arrangement: string | null;
  /** The exact colours to paint, top of the building first. */
  bands: string[];
  /**
   * Whether those colours go round the building or down it.
   *
   * Null for a flag chosen before layouts existed, which the render path
   * reads as the horizontal bands it was already painting.
   */
  layout: FlagLayout | null;
}

/** What may be recorded alongside the country. */
export interface FlagIntentDetail {
  arrangement?: string | null;
  bands?: readonly string[] | null;
  layout?: unknown;
}

const memory = new Map<string, FlagIntent>();

/** Bumped with the stored shape: v1 held a country code and nothing else. */
const LS_KEY = 'geotrust:spatial-flag-v2';
/** Enough for a city's worth of flagged buildings; oldest are dropped. */
const LS_MAX = 200;

type PersistedEntry = {
  countryCode: string;
  arrangement?: string | null;
  bands?: string[];
  layout?: FlagLayout | null;
  updatedAt: number;
};

function readStore(): Record<string, PersistedEntry> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, PersistedEntry>): void {
  try {
    const trimmed = Object.entries(store)
      .sort((a, b) => (b[1]?.updatedAt ?? 0) - (a[1]?.updatedAt ?? 0))
      .slice(0, LS_MAX);
    localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    /* a full or disabled store must not break a render */
  }
}

function normalizeIso(value: unknown): string | null {
  const s = String(value ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(s) ? s : null;
}

/** Colour strings only: anything else would reach a paint property. */
function normalizeBands(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((c): c is string => typeof c === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(c));
}

function normalizeArrangement(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return s ? s : null;
}

/** Load what a previous session knew, so a hard refresh still paints flags. */
function hydrate(): void {
  const store = readStore();
  for (const id of Object.keys(store)) {
    const entry = store[id];
    const iso = normalizeIso(entry?.countryCode);
    if (!iso) continue;
    memory.set(id, {
      countryCode: iso,
      arrangement: normalizeArrangement(entry?.arrangement),
      bands: normalizeBands(entry?.bands),
      layout: parseFlagLayout(entry?.layout),
    });
  }
}
hydrate();

/** What this object is known to be wearing, or null. */
export function flagIntentFor(objectId: string | null | undefined): FlagIntent | null {
  const id = String(objectId ?? '').trim();
  if (!id) return null;
  return memory.get(id) ?? null;
}

/**
 * Record (or clear) an intent known first-hand.
 *
 * Called by the editor the moment a flag is saved, so the building does
 * not have to wait for a detail fetch that may not happen until it is
 * clicked.
 */
export function rememberFlagIntent(
  objectId: string,
  countryCode: string | null,
  detail?: FlagIntentDetail,
): void {
  const id = String(objectId ?? '').trim();
  if (!id) return;

  const iso = normalizeIso(countryCode);
  const store = readStore();
  if (iso) {
    const bands = normalizeBands(detail?.bands);
    const arrangement = normalizeArrangement(detail?.arrangement);
    const layout = parseFlagLayout(detail?.layout);
    memory.set(id, { countryCode: iso, arrangement, bands, layout });
    store[id] = { countryCode: iso, arrangement, bands, layout, updatedAt: Date.now() };
  } else {
    memory.delete(id);
    delete store[id];
  }
  writeStore(store);
}

/**
 * Learn from a feature's metadata as it passes through normalisation.
 *
 * An empty bag is a summary response and is ignored; see the note at the
 * top of this file for why silence must not clear a known flag.
 */
export function learnFlagIntent(
  objectId: string | null | undefined,
  metadata: Record<string, unknown>,
): void {
  const id = String(objectId ?? '').trim();
  if (!id || Object.keys(metadata).length === 0) return;

  const raw = (metadata.surface_flag ?? metadata.surfaceFlag) as
    | {
      enabled?: unknown;
      countryCode?: unknown;
      country_code?: unknown;
      arrangement?: unknown;
      bands?: unknown;
      layout?: unknown;
    }
    | undefined;

  if (!raw || typeof raw !== 'object' || raw.enabled !== true) {
    // The object was asked about and has no flag: forget any we held.
    if (memory.has(id)) rememberFlagIntent(id, null);
    return;
  }

  const iso = normalizeIso(raw.countryCode ?? raw.country_code);
  if (!iso) return;

  const bands = normalizeBands(raw.bands);
  const arrangement = normalizeArrangement(raw.arrangement);
  const layout = parseFlagLayout(raw.layout);
  const held = memory.get(id);
  const unchanged = held
    && held.countryCode === iso
    && held.arrangement === arrangement
    && held.bands.join('|') === bands.join('|')
    && JSON.stringify(held.layout) === JSON.stringify(layout);
  if (unchanged) return;

  rememberFlagIntent(id, iso, { arrangement, bands, layout });
}

/** Test seam. */
export function _resetFlagIntents(): void {
  memory.clear();
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
